<template>
  <div class="text-left" v-if="event && !executing">
    <div class="mb-4">
      <mdb-row>
        <mdb-col col="6">
          <mdb-input label="Max # Attendees" type="number" v-model="event.attendeeLimit"/>
        </mdb-col>
        <mdb-col col="6">
          <mdb-input label="Max # Guests" type="number" v-model="event.guestLimit"/>
        </mdb-col>
      </mdb-row>
      <mdb-row v-if="event.isRepeatParent">
        <mdb-col col="6">
          <div class="form-check">
            <input aria-label="Attend all in series" class="form-control form-check-input"
                   id="attendAllInSeries" type="checkbox"
                   v-model="event.attendAllInSeries"/>
            <label for="attendAllInSeries">{{ capitalize($t('page.event.rsvp')) }} is for all in series.</label>
            <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
              <span slot="tip">{{ capitalize($t('page.event.rsvp')) }} in {{ $tc('common.label.event',1) }} creates
                {{ capitalize($t('page.event.rsvp')) }} for all
                {{ $tc('common.label.event',2) }} in the series.
              </span>
              <mdb-icon icon="info-circle" slot="reference"/>
            </mdb-tooltip>
          </div>
        </mdb-col>
      </mdb-row>
      <mdb-row>
        <mdb-col col="6">
          <mdb-input id="requireGuestNames" label="Require Guest Names?" name="requireGuestName"
                     type="checkbox" v-model="event.requireGuestNames"/>
        </mdb-col>
        <mdb-col col="6">
          <div class="form-check">
            <input aria-label="Attendee Counts by Type?" class="form-control form-check-input"
                   id="attendeeBreakdown" type="checkbox"
                   v-model="event.attendeeBreakdown"/>
            <label for="attendeeBreakdown">Attendee Counts by Type?</label>

            <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
              <span slot="tip">Do you wish to collect counts
                by type of person attending? (e.g., Adult, Child, Senior, etc)
              </span>
              <mdb-icon icon="info-circle" slot="reference"/>
            </mdb-tooltip>
          </div>
        </mdb-col>
      </mdb-row>
      <mdb-row>
        <mdb-col col="6">
          <div class="form-check">
            <input aria-label="Auto Charge?" class="form-control form-check-input"
                   id="autoCharge" type="checkbox"
                   v-model="event.autoCharge"/>
            <label for="autoCharge">Auto Charge?</label>

            <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
              <span slot="tip">If there's an event fee, do you want to create a charge
                to the member's account automatically? A payment to a charge can be recorded manually
                or electronically if you enable Stripe payments. You may also generate charges manually
                and still accept Stripe payments.
              </span>
              <mdb-icon icon="info-circle" slot="reference"/>
            </mdb-tooltip>
          </div>
        </mdb-col>
        <mdb-col col="6" v-if="!event.attendeeBreakdown">
          <mdb-input type="number" :step="0.01" label="Cost Per Person" v-model="event.costPerPerson"/>
        </mdb-col>
      </mdb-row>
      <mdb-row v-if="event.autoCharge">
        <mdb-col col="6">
          <div class="form-check">
            <input aria-label="Charge Payment Method Immediately?" class="form-control form-check-input"
                   id="chargeImmediately" type="checkbox"
                   v-model="event.chargeImmediately"/>
            <label for="chargeImmediately">Charge Payment Method Immediately?</label>

            <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
              <span slot="tip">Check if you have Stripe payments enabled and you wish to require payment before
                reservation is made. This will charge the member's credit card on file immediately. If no credit card
                is on file, they will be required to add one before continuing with the reservation.
              </span>
              <mdb-icon icon="info-circle" slot="reference"/>
            </mdb-tooltip>
          </div>
        </mdb-col>
      </mdb-row>

      <mdb-row>
        <mdb-col col="6" v-if="event.autoCharge">
          <div class="form-check">
            <input aria-label="No Refunds?" class="form-control form-check-input"
                   id="noAutoRefund" type="checkbox"
                   v-model="event.noAutoRefund"/>
            <label for="noAutoRefund">No Refunds</label>

            <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
              <span slot="tip">If there's an event fee with Auto Charge enabled, do not generate a refund when someone
                cancels.
              </span>
              <mdb-icon icon="info-circle" slot="reference"/>
            </mdb-tooltip>
          </div>
        </mdb-col>
      </mdb-row>



      <mdb-row v-if="event.attendeeBreakdown">
        <mdb-col col="6">
          <mdb-input type="text" label="Person Type 1" v-model="event.attendeeLabel1"/>
        </mdb-col>
        <mdb-col col="6" v-if="event.attendeeLabel1">
          <mdb-input type="number" :step="0.01" :label="event.attendeeLabel1 + ' cost'" v-model="event.costType1"/>
        </mdb-col>
      </mdb-row>
      <mdb-row v-if="event.attendeeBreakdown">
        <mdb-col col="6">
          <mdb-input type="text" label="Person Type 2" v-model="event.attendeeLabel2"/>
        </mdb-col>
        <mdb-col col="6" v-if="event.attendeeLabel2">
          <mdb-input type="number" :step="0.01" :label="event.attendeeLabel2 + ' cost'" v-model="event.costType2"/>
        </mdb-col>
      </mdb-row>
      <mdb-row v-if="event.attendeeBreakdown">
        <mdb-col col="6">
          <mdb-input type="text" label="Person Type 3" v-model="event.attendeeLabel3"/>
        </mdb-col>
        <mdb-col col="6" v-if="event.attendeeLabel3">
          <mdb-input type="number" :step="0.01" :label="event.attendeeLabel3 + ' cost'" v-model="event.costType3"/>
        </mdb-col>
      </mdb-row>
      <mdb-row v-if="event.attendeeBreakdown">
        <mdb-col col="6">
          <mdb-input type="text" label="Person Type 4" v-model="event.attendeeLabel4"/>
        </mdb-col>
        <mdb-col col="6" v-if="event.attendeeLabel4">
          <mdb-input type="number" :step="0.01" :label="event.attendeeLabel4 + ' cost'" v-model="event.costType4"/>
        </mdb-col>
      </mdb-row>
      <mdb-row v-if="event.attendeeBreakdown">
        <mdb-col col="6">
          <mdb-input type="text" label="Person Type 5" v-model="event.attendeeLabel5"/>
        </mdb-col>
        <mdb-col col="6" v-if="event.attendeeLabel5">
          <mdb-input type="number" :step="0.01" :label="event.attendeeLabel5 + ' cost'" v-model="event.costType5"/>
        </mdb-col>
      </mdb-row>

      <mdb-btn @click="doSave" color="success" icon="save">Save</mdb-btn>
      <mdb-btn @click="doCancel" color="danger" icon="times-circle">Cancel</mdb-btn>
    </div>
    <div v-if="showSeriesModal">
      <series-modal @closeModal="showSeriesModal=false" @seriesChoice="setSeriesChoice"/>
    </div>

  </div>

</template>

<script>
import api from '../../services/groupsPlaceApi.js'
import {mdbBtn, mdbCol, mdbIcon, mdbInput, mdbRow, mdbTooltip} from 'mdbvue';
import SeriesModal from "./SeriesModal";

export default {
  name: 'Event-Settings-Editor',
  components: {
    mdbBtn,
    mdbIcon,
    mdbInput,
    mdbRow,
    mdbCol,
    mdbTooltip,
    SeriesModal
  },
  props: {
    id: {
      type: Number,
      default: null
    }
  },
  computed: {
    userDetails() {
      return this.$store.state.userDetails
    },
    group() {
      return this.$store.state.currentGroup
    },
    settings() {
      return this.$store.state.settings
    },
    executing() {
      return this.$store.state.executing
    }
  },
  data() {
    return {
      event: null,
      showSeriesModal: false,
      seriesChoice: null
    }
  },
  mounted() {
    this.$store.dispatch("setExecuting", true);
    api.get("/event/attendeeRules/" + this.id).then(
      (results) => {
        this.event = results.data;
      },
      (error) => {
        this.$notify.error({message: "Could not load rules. " + error.response.data, timeOut: 20000})
      }).then(() => {
      this.$store.dispatch("setExecuting", false)
    })
  },
  methods: {
    doCancel() {
      this.$emit("cancel")
    },
    setSeriesChoice(choice) {
      this.event.series = choice
      this.doSave()
    },
    doSave() {
      if (this.event.repeats && !this.event.series && this.event.status != "Draft") {
        this.showSeriesModal = true
        return
      }
      if (!this.event.autoCharge && this.event.chargeImmediately) this.event.chargeImmediately = false

      this.$store.dispatch("setExecuting", true);
      api.post('/event/attendeeRules/' + this.id, this.event).then(
        (results) => {
          this.$emit("save")
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 10000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
        this.showSeriesModal = false
        this.seriesChoice = null
      })
    }
  }
}
;
</script>
